import {
  ICallEvent,
  INTERACTION_TYPE_LABEL_MAP,
  InteractionType,
  MentionResourceType,
  interactionTypeDisplayMap,
} from '@principle-theorem/principle-core/interfaces';
import { isObject, type WithActors } from '@principle-theorem/shared';

export interface ICallEventDisplay {
  label: string;
  icon: string;
}

export class CallEvent {
  /**
   * Resolves the presentation ({ label, icon }) for a call event.
   *
   * The icon is always the source-agnostic `InteractionType.CallEvent` glyph read
   * from `interactionTypeDisplayMap` (the single source of truth — ADR-0009). The
   * label is resolved from the event's `createdBy` source (ADR-0011):
   *
   * - API-key source (`createdBy.type === MentionResourceType.ApiKey`): the caller's
   *   pre-resolved `resolvedSourceLabel`, resolved LIVE upstream from the key's
   *   `sourceLabel` (management override) then its own `label` (option B, ADR-0011),
   *   falling back to the `INTERACTION_TYPE_LABEL_MAP` default when blank/absent.
   *   The stale `createdBy.name` snapshot is never used for API-key sources.
   * - Named non-API source (e.g. a partner app): the source's own `name`.
   * - Anything else (undefined, a `SystemActors` string such as 'unknown', or a
   *   malformed value): the source-agnostic `INTERACTION_TYPE_LABEL_MAP` default (ADR-0009).
   *
   * Pure, synchronous, and framework-free — the caller resolves
   * `resolvedSourceLabel` upstream so this stays usable outside Angular. The event's
   * `createdBy` is a persistence-owned actor field (`WithActors`), populated on write.
   */
  static display(
    event: WithActors<ICallEvent>,
    resolvedSourceLabel?: string
  ): ICallEventDisplay {
    const icon = interactionTypeDisplayMap[InteractionType.CallEvent];
    const defaultLabel = INTERACTION_TYPE_LABEL_MAP[InteractionType.CallEvent];
    const createdBy = event.createdBy;

    if (isObject(createdBy) && createdBy.type === MentionResourceType.ApiKey) {
      return { label: resolvedSourceLabel?.trim() || defaultLabel, icon };
    }

    if (
      isObject(createdBy) &&
      typeof createdBy.name === 'string' &&
      createdBy.name.length > 0
    ) {
      return { label: createdBy.name, icon };
    }

    return { label: defaultLabel, icon };
  }
}
